import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { Plus } from "lucide-react";
import { useState } from "react";
import { initials } from "./data.js";
import { cn } from "./cn.js";

export const CARD_W = 216;
export const CARD_H = 78;

const TILT = { stiffness: 260, damping: 18, mass: 0.5 };

export function NodeCard({
  node,
  mx,
  my,
  depth,
  order,
  isRoot,
  selected,
  dimmed,
  radial,
  cardScale,
  onSelect,
  onAddChild,
  isLight,
  C,
}) {
  const [hovered, setHovered] = useState(false);

  const tangent = useTransform([mx, my], (latest) => {
    const [x, y] = latest;
    if (Math.abs(x) + Math.abs(y) < 0.001) return 0;
    const a = (Math.atan2(y, x) * 180) / Math.PI;
    return a > 90 || a < -90 ? a + 180 : a;
  });

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [13, -13]), TILT);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-17, 17]), TILT);
  const lift = useSpring(0, TILT);

  const glareX = useTransform(px, [-0.5, 0.5], ["0%", "100%"]);
  const glareY = useTransform(py, [-0.5, 0.5], ["0%", "100%"]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX} ${glareY}, rgba(255,255,255,0.16), transparent 55%)`;
  const shadowStrength = useTransform(lift, [0, 1], [0, 1]);
  const shadow = useMotionTemplate`0 ${useTransform(
    shadowStrength,
    [0, 1],
    [16, 42]
  )}px ${useTransform(shadowStrength, [0, 1], [40, 70])}px -18px rgba(0,0,0,0.92)`;

  const handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };

  const handleEnter = () => {
    setHovered(true);
    lift.set(1);
  };

  const handleLeave = () => {
    setHovered(false);
    px.set(0);
    py.set(0);
    lift.set(0);
  };

  return (
    <motion.div
      style={{
        x: mx,
        y: my,
        width: CARD_W,
        zIndex: selected ? 30 : hovered ? 25 : 10,
        pointerEvents: dimmed ? "none" : "auto",
      }}
      className="absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2"
      initial={{ opacity: 0, scale: cardScale * 0.6 }}
      animate={{
        opacity: dimmed ? 0.07 : 1,
        scale: dimmed ? cardScale * 0.9 : cardScale,
        filter: dimmed ? "saturate(0)" : "saturate(1)",
      }}
      transition={{
        opacity: { duration: dimmed ? 0.5 : 0.35, delay: dimmed ? 0 : Math.min(depth * 0.08, 0.5) },
        filter: { duration: 0.45 },
        scale: dimmed
          ? { type: "spring", stiffness: 200, damping: 24 }
          : { type: "spring", stiffness: 240, damping: 21, delay: Math.min(order * 0.03, 0.6) },
      }}
    >
      <motion.div
        style={{ rotate: radial ? tangent : 0 }}
      >
        <div style={{ perspective: 850 }}>
          <motion.div
            role="button"
            tabIndex={0}
            onPointerMove={handleMove}
            onPointerEnter={handleEnter}
            onPointerLeave={handleLeave}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(node.id);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSelect(node.id);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              rotateX,
              rotateY,
              boxShadow: shadow,
              transformStyle: "preserve-3d",
            }}
            className={cn(
              "group relative w-full cursor-pointer rounded-2xl border p-3 text-left transition-[border-color,background-color] duration-300",
              selected
                ? isLight
                  ? "border-[rgba(37,99,235,0.55)] bg-white"
                  : "border-[rgba(210,241,85,0.65)] bg-[rgba(20,22,16,0.92)]"
                : isLight
                ? "border-slate-200 bg-white/90 hover:border-slate-300 hover:bg-white"
                : "border-white/[0.09] bg-[rgba(16,17,21,0.86)] hover:border-white/[0.24] hover:bg-[rgba(22,24,30,0.92)]"
            )}
          >
            <motion.span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{ backgroundImage: glare }}
            />
            {selected && (
              <span
                className="pointer-events-none absolute -inset-px rounded-2xl"
                style={{
                  boxShadow: isLight
                    ? "0 0 0 1px rgba(37,99,235,0.25), 0 0 34px -6px rgba(37,99,235,0.35)"
                    : "0 0 0 1px rgba(210,241,85,0.3),0 0 34px -6px rgba(210,241,85,0.45)",
                }}
              />
            )}

            <span
              className="absolute left-3 top-3 bottom-3 w-[3px] rounded-full"
              style={{
                background: `linear-gradient(180deg, ${node.color}, ${node.color}30)`,
                transform: "translateZ(18px)",
              }}
            />

            <button
              aria-label={`Add a direct report to ${node.name}`}
              title="Add direct report"
              onClick={(e) => {
                e.stopPropagation();
                onAddChild(node.id);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ transform: "translateZ(30px)" }}
              className={cn(
                "absolute -bottom-3 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border shadow-lg opacity-0 transition-all duration-200 group-hover:opacity-100",
                isLight
                  ? "border-slate-200 bg-white text-slate-400 hover:border-[rgba(37,99,235,0.55)] hover:text-[#2563eb]"
                  : "border-white/15 bg-[#17181d] text-white/60 hover:border-[rgba(210,241,85,0.6)] hover:text-[#d2f155]"
              )}
            >
              <Plus size={12} strokeWidth={2.5} />
            </button>

            <div className="flex items-center gap-3 pl-2.5" style={{ transform: "translateZ(26px)" }}>
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-['Space_Grotesk'] text-[13px] font-semibold tracking-wide"
                style={{
                  background: isLight
                    ? `linear-gradient(140deg, ${node.color}22, ${node.color}08)`
                    : `linear-gradient(140deg, ${node.color}30, ${node.color}0a)`,
                  borderColor: isLight ? `${node.color}66` : `${node.color}55`,
                  color: node.color,
                }}
              >
                {initials(node.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block truncate text-[13px] font-semibold leading-tight",
                    isLight ? "text-slate-900" : "text-white"
                  )}
                >
                  {node.name}
                </span>
                <span
                  className={cn(
                    "mt-0.5 block truncate text-[11px] leading-tight",
                    isLight ? "text-slate-500" : "text-white/80"
                  )}
                >
                  {node.role}
                </span>
              </span>
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{
                  background: node.color,
                  boxShadow: isLight ? `0 0 8px ${node.color}55` : `0 0 8px ${node.color}90`,
                }}
              />
            </div>

            {isRoot && (
              <span
                className={cn(
                  "absolute -top-2 right-3 rounded-full border px-1.5 py-px font-mono text-[8.5px] uppercase tracking-[0.18em]",
                  isLight
                    ? "border-slate-200 bg-white text-slate-500"
                    : "border-white/10 bg-[#101114] text-white/50"
                )}
                style={{ transform: "translateZ(30px)" }}
              >
                Root
              </span>
            )}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
